import { type FragmentOf, graphql } from "@/data-access/graphql";

/**
 * Fragment for social metrics data
 * Used by both discovery table and news feed cards
 */
const SocialMetricsFragment = graphql(`
  fragment SocialMetricsFragment on SocialMetricsType @_unmask {
    alexaRank
    angryCount
    facebookComments
    facebookLikes
    facebookReactions
    facebookShares
    hahaCount
    loveCount
    numLinkingDomains
    pinterestShares
    redditEngagements
    sadCount
    twitterShares
    wowCount
  }
`);

/**
 * Fragment for first mention data
 * Used by both discovery table and news feed cards
 */
const FirstMentionFragment = graphql(`
  fragment FirstMentionFragment on FeedArticleMentionType @_unmask {
    term
    snippet
  }
`);

/**
 * Fragment for cursor-based pagination info (Relay Connection)
 * Used for news feed cards with cursor pagination
 */
export const PageInfoFragment = graphql(`
  fragment PageInfoFragment on PageInfo @_unmask {
    startCursor
    endCursor
    hasNextPage
    hasPreviousPage
  }
`);

/**
 * Fragment for offset pagination info
 * Used for discovery table with offset pagination
 */
export const OffsetPaginationInfoFragment = graphql(`
  fragment OffsetPaginationInfoFragment on OffsetPaginationInfo @_unmask {
    limit
    offset
  }
`);

/**
 * Core fragment for FeedArticleProjection fields
 * Used by both discovery table and news feed cards
 * Contains all common fields needed for displaying articles
 */
export const FeedArticleProjectionFragment = graphql(
  `
    fragment FeedArticleProjectionFragment on FeedArticleProjectionType @_unmask {
      id
      feedArticleId
      articleId
      headline
      url
      datePublished
      authors
      contentCategory
      language
      prominence
      isLede
      impactScore
      publisher
      publisherId
      publisherCountry
      publisherRegion
      publisherTier
      publisherDomainAuthority
      publisherReadership
      publisherLogo
      sentimentPolarity
      sentimentRationale
      sentimentPolarityOriginal
      sentimentRationaleOriginal
      socialEngagement
      socialMetrics {
        ...SocialMetricsFragment
      }
      storyId
      storyName
      domain
      articleImage
      summary
      firstMention {
        ...FirstMentionFragment
      }
      searchMatch
      articleTags: feedArticleTags {
        id
        tag
      }
    }
  `,
  [SocialMetricsFragment, FirstMentionFragment],
);

/** Shape of feed article projection data (fragment result). Use for components that render projection data from any source (query or subscription). */
export type FeedArticleProjectionData = FragmentOf<
  typeof FeedArticleProjectionFragment
>;
